import { memo, useContext, useMemo, useRef } from "react";
import DOMPurify from "dompurify";
import markdownit from "markdown-it";

import {
  extractCitationClaim,
  markPatientIdTags,
  moveSourceMarkersToLineEnd,
  processHtmlSources,
  renderPatientLinks,
  stripPatientIdTags,
} from "../../../pages/Neo/citations/sourceMarkers";
import { useStreamWordReveal } from "../../../pages/Neo/useStreamWordReveal";
import { usePatientPeek } from "../../neo/PatientPeek/PatientPeekContext";
import { CopilotCitationContext } from "./citationContext";
import styles from "./styles.module.scss";

const markdown = markdownit({
  html: false,
  linkify: true,
  typographer: true,
  breaks: true,
});

const defaultLinkOpen =
  markdown.renderer.rules.link_open ??
  ((tokens, index, options, _env, renderer) =>
    renderer.renderToken(tokens, index, options));

markdown.renderer.rules.link_open = (tokens, index, options, env, renderer) => {
  tokens[index].attrSet("target", "_blank");
  tokens[index].attrSet("rel", "noopener noreferrer");
  return defaultLinkOpen(tokens, index, options, env, renderer);
};

const resolveNothing = () => undefined;
const SOURCE_CHIP_CLASS = `${styles.sourceBadge} ${styles.sourceChip}`;

const MarkdownMessage = ({
  source,
  streaming = false,
}: {
  source: string;
  /** This is the live reply: fade newly-streamed words in. */
  streaming?: boolean;
}) => {
  const citations = useContext(CopilotCitationContext);
  const resolve = citations?.resolve ?? resolveNothing;
  const medRecVisible = citations?.medRecVisible ?? false;
  const patientPeek = usePatientPeek();
  const linkPatients = !!patientPeek;
  const rootRef = useRef<HTMLDivElement>(null);

  const html = useMemo(() => {
    // Collect each line's citation markers after its punctuation, then swap
    // them for numbered chips. Markers that don't resolve against this
    // message's tool outputs are dropped (no provider → all are dropped).
    // Patient-name id tags (``<[p-id]>…<[]>``) never reach the rendered HTML
    // raw (markdown-it escapes, not drops, them): inside the patient-peek
    // provider they become clickable names, elsewhere they're stripped.
    const tagged = linkPatients
      ? markPatientIdTags(source)
      : { text: stripPatientIdTags(source), links: [] };
    const prepared = moveSourceMarkersToLineEnd(
      tagged.text.replace(/\n+$/, "")
    );
    const rendered = processHtmlSources(
      markdown.render(prepared),
      resolve,
      medRecVisible,
      SOURCE_CHIP_CLASS
    );
    const linked = linkPatients
      ? renderPatientLinks(rendered, tagged.links, styles.patientLink)
      : rendered;
    return DOMPurify.sanitize(linked, { ADD_ATTR: ["target", "rel"] });
  }, [source, resolve, medRecVisible, linkPatients]);

  useStreamWordReveal(
    rootRef,
    html,
    streaming,
    styles.streamWord,
    styles.streamSettled
  );

  return (
    <div ref={rootRef}>
      <div
        className={styles.markdown}
        dangerouslySetInnerHTML={{ __html: html }}
        onClick={(event) => {
          const patient = (event.target as Element).closest?.(
            "[data-neo-patient]"
          );
          if (patient && patientPeek) {
            // A name inside a markdown link must open the peek, not the URL.
            event.preventDefault();
            const patientId = Number(patient.getAttribute("data-neo-patient"));
            if (patientId) {
              patientPeek.openPatient({
                patientId,
                name: patient.textContent?.trim() || undefined,
              });
            }
            return;
          }
          if (!citations) return;
          const chip = (event.target as Element).closest?.(
            "[data-neo-sources]"
          );
          if (!chip) return;
          const ids = (chip.getAttribute("data-neo-sources") ?? "")
            .split(",")
            .map((id) => id.trim())
            .filter(Boolean);
          if (ids.length) citations.onOpen(ids, extractCitationClaim(chip));
        }}
      />
    </div>
  );
};

export default memo(MarkdownMessage);
